import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  CircularProgress,
  Switch,
  Snackbar,
  Alert,
  Divider,
  AppBar,
  Toolbar,
  Card,
  CardContent,
} from '@mui/material';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import api from '../api/api';
import { obtenerDatosToken } from '../utils/authToken'; // Asegúrate de ajustar la ruta según tu estructura

const ConfiguracionReglaPuntos = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);

  const [formData, setFormData] = useState({
    id_pet: '',
    monto_minimo: '0.00',
    monto_por_punto: '1.00',
    valor_punto_canje: '0.0100',
    habilitado: true,
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Cargar token y claims de usuario
  useEffect(() => {
    const tokenData = obtenerDatosToken();
    if (tokenData) {
      setUserIdPet(tokenData.id_pet || null);
      setUserIdRol(tokenData.id_rol || null);
    } else {
      const errorMsg = 'No se encontró información de autenticación válida.';
      setError(errorMsg);
      mostrarSnackbar(errorMsg, 'error');
    }
  }, []);

  // Función helper para mostrar snackbar
  const mostrarSnackbar = (mensaje, severidad = 'success') => {
    setSnackbarMessage(mensaje);
    setSnackbarSeverity(severidad);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Cargar configuración existente desde la API
  const fetchReglaPuntos = async () => {
    if (!userIdPet) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/puntos-reglas/mi-pet');
      if (response.data) {
        setFormData({
          id_pet: response.data.id_pet ?? userIdPet,
          monto_minimo: response.data.monto_minimo ?? '0.00',
          monto_por_punto: response.data.monto_por_punto ?? '1.00',
          valor_punto_canje: response.data.valor_punto_canje ?? '0.0100',
          habilitado: Boolean(response.data.habilitado),
        });
      }
    } catch (err) {
      if (err.response?.status === 404) {
        // No hay regla configurada todavía, inicializar con defaults
        setFormData((prev) => ({ ...prev, id_pet: userIdPet }));
      } else {
        console.error('Error al cargar la regla de puntos:', err);
        const msg = 'Error al cargar la regla de puntos: ' + (err.response?.data?.detail || err.message);
        setError(msg);
        mostrarSnackbar(msg, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userIdPet) {
      fetchReglaPuntos();
    }
  }, [userIdPet]);

  // Manejador para el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        id_pet: Number(userIdPet),
        monto_minimo: parseFloat(formData.monto_minimo) || 0,
        monto_por_punto: parseFloat(formData.monto_por_punto) || 1,
        valor_punto_canje: parseFloat(formData.valor_punto_canje) || 0,
        habilitado: formData.habilitado,
      };

      const response = await api.post('/puntos-reglas/', payload);
      
      if (response.data) {
        setFormData({
          id_pet: response.data.id_pet,
          monto_minimo: response.data.monto_minimo,
          monto_por_punto: response.data.monto_por_punto,
          valor_punto_canje: response.data.valor_punto_canje,
          habilitado: Boolean(response.data.habilitado),
        });
        mostrarSnackbar('Regla de puntos guardada exitosamente.', 'success');
      }
    } catch (err) {
      console.error('Error al guardar la regla de puntos:', err);
      const msg = 'Error al guardar configuración: ' + (err.response?.data?.detail || err.message);
      mostrarSnackbar(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Configuración del Programa de Puntos
          </Typography>
          <Button
            color="inherit"
            startIcon={<RefreshIcon />}
            onClick={fetchReglaPuntos}
            disabled={loading}
          >
            Recargar
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" disableGutters>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error && !userIdPet ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              Parámetros de Conversión y Canje
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Define las equivalencias de acumulación y valor de cada punto para tus clientes.
            </Typography>

            <Divider sx={{ mb: 4 }} />

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Monto Mínimo de Compra ($)"
                    type="number"
                    fullWidth
                    inputProps={{ step: "0.01", min: "0" }}
                    value={formData.monto_minimo}
                    onChange={(e) => setFormData({ ...formData, monto_minimo: e.target.value })}
                    helperText="Monto mínimo para comenzar a acumular puntos"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Monto consumido por punto ($)"
                    type="number"
                    fullWidth
                    inputProps={{ step: "0.01", min: "0.01" }}
                    value={formData.monto_por_punto}
                    onChange={(e) => setFormData({ ...formData, monto_por_punto: e.target.value })}
                    helperText="Monto en dinero necesario para generar 1 punto (Ej. $1.00 = 1 Punto)"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valor de Cada Punto al Canjear ($)"
                    type="number"
                    fullWidth
                    inputProps={{ step: "0.0001", min: "0.0001" }}
                    value={formData.valor_punto_canje}
                    onChange={(e) => setFormData({ ...formData, valor_punto_canje: e.target.value })}
                    helperText="Valor monetario equivalente de 1 punto (Ej. $0.0100)"
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Card variant="outlined" sx={{ height: '100%', display: 'flex', alignItems: 'center', px: 2 }}>
                    <CardContent sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: '8px !important' }}>
                      <Box>
                        <Typography variant="subtitle2">Estado del Programa</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formData.habilitado ? 'Acumulación y Canje Activos' : 'Programa Deshabilitado'}
                        </Typography>
                      </Box>
                      <Switch
                        checked={formData.habilitado}
                        onChange={(e) => setFormData({ ...formData, habilitado: e.target.checked })}
                        color="primary"
                      />
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar Configuración'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default ConfiguracionReglaPuntos;